import { z } from 'zod';

/**
 * MoyenDraft (ex-`ArgumentDraft`, renommé 2026-07-06, chantier « prétentions ») —
 * le MOYEN au sens des art. 768/954 CPC : le raisonnement en fait et en droit qui
 * FONDE une ou plusieurs prétentions. Ce que le pipeline appelait « argument » est
 * juridiquement un moyen ; les alias `ArgumentDraft*` restent exportés le temps de
 * renommer les sites consommateurs (~30 fichiers backend).
 *
 * Hiérarchie complète : `PretentionDraft` (ce que le client demande, cf.
 * `pretention-draft.ts`) → moyens rattachés via `pretention_ids` (rang
 * principal/subsidiaire AU SEIN de la prétention) → jurisprudences/pièces.
 *
 * Le score `confidence` traduit la qualité (un moyen fragile n'est pas filtré,
 * l'aval décide retenu/écarté). Pas de `search_query_jp` : le moyen auto-portant
 * suffit, le KeywordGenerator génère ses propres queries.
 */

/**
 * Un article de loi cité comme fondement d'un moyen.
 *
 * Volontairement réduit à `{ code, article }` — PAS de hiérarchie
 * `principal`/`subsidiaire`. Un article n'est pas « principal » ou
 * « subsidiaire » dans l'absolu : c'est le MOYEN qui peut l'être, pas la
 * disposition (la subsidiarité entre moyens vit désormais dans `MoyenDraft.rang`).
 * L'ancien champ `role` poussait le LLM à empiler un article de « repli »
 * (typiquement 1240 C.civ.) hors du terrain juridique du moyen, juste pour
 * étoffer. On ne garde que les articles CLÉS qui gouvernent réellement le
 * terrain du moyen (cf. TICKET-305).
 */
// NB — pas de `.strict()` sur les schémas de ce fichier : ils sont consommés
// comme STRUCTURED OUTPUT LLM (planner `LegalPlannerOutputSchema`, refiner
// `RefinerMoyenSchema`). `.strict()` rejette toute clé parasite que le modèle
// ajoute spontanément (à n'importe quel niveau d'imbrication) → le LLM réussit
// mais le parsing échoue → `AllProvidersExhausted` sur toute la chaîne. Incident
// staging 2026-07-06 (refiner, glm-5-turbo + deepseek-v4-flash) — même classe que
// #83 (bordereau). Défaut Zod `.strip()` : les clés inconnues sont DROPPÉES. Cf.
// packages/shared-types/CLAUDE.md « .strict() sur un schéma de sortie LLM ».
export const FondementCiteSchema = z.object({
  code: z.string(),
  article: z.string(),
});
export type FondementCite = z.infer<typeof FondementCiteSchema>;

export const LegalReasoningSchema = z.object({
  majeure: z.string(),                             // principe juridique posé + articles, RÉDIGÉ
  mineure: z.string(),                             // application à l'espèce, RÉDIGÉE
});                                                // non-strict : sortie LLM, cf. note en tête
export type LegalReasoning = z.infer<typeof LegalReasoningSchema>;

/**
 * Rang d'un moyen AU SEIN de la (des) prétention(s) qu'il fonde : le moyen le
 * plus fort en `principal`, les terrains alternatifs en `subsidiaire`
 * (concentration des moyens, Ass. plén. 7 juill. 2006, Cesareo). Distinct de
 * `PretentionRang` (subsidiarité ENTRE prétentions).
 */
export const MoyenRangSchema = z.enum(['principal', 'subsidiaire']);
export type MoyenRang = z.infer<typeof MoyenRangSchema>;

/**
 * Champs `pretention_ids` / `rang` : introduits par le chantier prétentions —
 * OPTIONNELS au parse pour la compat des blobs antérieurs (jobs Redis en vol,
 * research results persistés, rows `pipeline_log`). Le planner les produit
 * toujours ; l'aval doit tolérer `undefined` sur données legacy.
 *
 * Champ `pretention` : TRANSITIONNEL/DÉRIVÉ — texte backfillé depuis les `objet`
 * des prétentions référencées (`derivePretentionText`, SSOT = `PretentionDraft`).
 * Encore consommé par l'aval (prompts keyword/step2, aggregator, controller).
 * Ne plus l'écrire à la main.
 */
export const MoyenDraftSchema = z.object({
  id: z.string(),                                  // 'arg-1', 'arg-2', ... (format conservé)
  titre: z.string(),
  pretention: z.string(),                          // DÉRIVÉ — cf. TSDoc ci-dessus
  pretention_ids: z.array(z.string()).optional(),  // refs 'pret-N' — absent sur données legacy
  rang: MoyenRangSchema.optional(),                // absent sur données legacy
  theme_ids: z.array(z.string()),                  // vestige V1-bis : toujours [moyen.id]
  fondements_cites: z.array(FondementCiteSchema),
  legal_reasoning: LegalReasoningSchema,
  confidence: z.number().min(0).max(1),
});                                                // non-strict : sortie LLM, cf. note en tête
export type MoyenDraft = z.infer<typeof MoyenDraftSchema>;

/** @deprecated Renommage 2026-07-06 (argument → moyen) — utiliser `MoyenDraftSchema`. */
export const ArgumentDraftSchema = MoyenDraftSchema;
/** @deprecated Renommage 2026-07-06 (argument → moyen) — utiliser `MoyenDraft`. */
export type ArgumentDraft = MoyenDraft;

/** La clé wire reste `arguments` (compat API/jobs) — chaque entrée est un MOYEN. */
export const MoyensListSchema = z.object({
  arguments: z.array(MoyenDraftSchema).min(1),
});                                                // non-strict : sortie LLM, cf. note en tête
export type MoyensList = z.infer<typeof MoyensListSchema>;

/** @deprecated Renommage 2026-07-06 (argument → moyen) — utiliser `MoyensListSchema`. */
export const ArgumentsListSchema = MoyensListSchema;
/** @deprecated Renommage 2026-07-06 (argument → moyen) — utiliser `MoyensList`. */
export type ArgumentsList = MoyensList;
